import type { BasicProductModelV4, CustomerDetailsReqModelV4, OrderCreateDraftReqModel, OrderCreateDraftReqModelV4, OrderDraftEPrescriptionModelV4, OrderDraftModelV4, OrderDraftPrescriptionModelV4 } from '@develop/ia-api-library'
import {
  createDraftV4 as createDraftAPI,
  createDraft as createDraftLegacyAPI,
  http,
  setCustomerDetailsV4 as storeCustomerDetailsAPI,
  updateCartV4 as updateCartAPI,
} from '@develop/ia-api-library'
import { StorageSerializers } from '@vueuse/core'
import { defineStore } from 'pinia'
import type { GuestData } from '@/models/guest'
import type { CheckoutStorage } from '@/models/checkout'
import { LocalStorage } from '~/models'

export const useCheckoutStore = defineStore('checkout-store', () => {
  const authStore = useAuthStore()
  const cartStore = useCartStore()
  const anonymousUser = useAnonymousUser()
  const currentPharmacyStore = useCurrentPharmacyStore()
  const couponStore = useCouponStore()
  const deliveryStore = useDeliveryStore()
  const paymentStore = usePaymentStore()
  const orderStore = useOrderStore()
  const modalManager = useModalManager()

  const { fecApiKey, isLegacy } = useEnvs()
  const { locale } = useTranslation()

  const checkoutStatus = ref<OrderDraftModelV4>()
  const setCheckoutStatus = (data: OrderDraftModelV4) => checkoutStatus.value = data

  const checkoutLocalStorage = useLocalStorage<CheckoutStorage>(LocalStorage.CheckoutStorage, {
    draftId: undefined,
    owner: undefined,
    activeStep: undefined,
    guestData: undefined,
    isPreviousStepCustomer: false,
  }, {
    serializer: StorageSerializers.object,
    initOnMounted: true,
  })

  const messages = computed(() => checkoutStatus.value?.messages ?? [])

  const draftId = computed({
    // get draftId from checkoutLocalStorage or legacy vuex (for legacy FEC)
    get: () => checkoutLocalStorage.value.draftId ?? legacyVuex.value.draft?.draftId,
    set: draftId => set(checkoutLocalStorage, { ...checkoutLocalStorage.value, draftId }),
  })

  const owner = computed({
    get: () => checkoutLocalStorage.value.owner ?? legacyVuex.value.owner,
    set: owner => set(checkoutLocalStorage, { ...checkoutLocalStorage.value, owner }),
  })

  const activeStep = computed({
    get: () => checkoutLocalStorage.value.activeStep,
    set: activeStep => set(checkoutLocalStorage, { ...checkoutLocalStorage.value, activeStep }),
  })

  const isPreviousStepCustomer = computed({
    get: () => checkoutLocalStorage.value.isPreviousStepCustomer,
    set: isPreviousStepCustomer => set(checkoutLocalStorage, { ...checkoutLocalStorage.value, isPreviousStepCustomer }),
  })

  const legacyVuex = useLegacyVuex()

  const anonymousUserId = computed(() => authStore.token ? undefined : anonymousUser.getToken())

  watch(draftId, (newDraftId) => {
    if (!newDraftId || newDraftId === legacyVuex.value.draft?.draftId) {
      return
    }
    const draft = { draftId: newDraftId }
    set(legacyVuex, { ...legacyVuex.value, draft, owner: anonymousUser.getToken() })
  })

  // read vuex draft.draftId and write to checkoutLocalStorage.draftId
  watch(() => legacyVuex.value.draft?.draftId, (newDraftId) => {
    if (!newDraftId || newDraftId === draftId.value) {
      return
    }
    set(draftId, newDraftId)
  })

  const setGuestData = (guestData: GuestData | undefined) =>
    set(checkoutLocalStorage, { ...checkoutLocalStorage.value, guestData })

  const guestData = computed(() => checkoutLocalStorage.value.guestData)

  const isLoading = ref(false)
  const setIsLoading = (value: boolean) => isLoading.value = value

  const isDataReady = ref(false)

  type ProductPriceType = 'products' | 'non-payable-products' | 'coupon' | 'on-duty' | 'delivery' | 'total-cart' | 'total'

  const findProductPriceByType = (type: ProductPriceType) => checkoutStatus.value?.price.items.find(p => p.type === type)
  const useProductPriceByType = (type: ProductPriceType) => computed(() => findProductPriceByType(type))

  const productsPrice = useProductPriceByType('products')
  const nonPayableProductsPrice = useProductPriceByType('non-payable-products')
  const couponPrice = useProductPriceByType('coupon')
  const onDutyPrice = useProductPriceByType('on-duty')
  const deliveryPrice = useProductPriceByType('delivery')
  // totalCartPrice is the sum of all products and coupons, excluding further step costs such as delivery
  const totalCartPrice = useProductPriceByType('total-cart')
  const totalPrice = useProductPriceByType('total')

  const adjustDate = (dateIso: string) => {
    const date = new Date(dateIso)
    const offsetHours = String(Math.floor(date.getTimezoneOffset() / 60)).padStart(2, '0')
    const offsetMinutes = String(date.getTimezoneOffset() % 60).padStart(2, '0')
    const timezoneOffset = `${(date.getTimezoneOffset() < 0 ? '+' : '-')
        + offsetHours.replace('-', '')
        }:${offsetMinutes}`
    const dateFormatted = useDateFormat(date, 'YYYY-MM-DDTHH:mm:ss')
    return `${dateFormatted.value}${timezoneOffset}`
  }

  const cartRequestData = computed<OrderCreateDraftReqModelV4>(() => {
    const products: BasicProductModelV4[] = cartStore.cartData.products.map(({ pzn, price, amount }) => ({
      pzn,
      price,
      amount,
    }))

    const prescriptions: OrderDraftPrescriptionModelV4[] = cartStore.cartData.prescriptions.map(({ createdAt, filename, note }) => ({
      filename,
      note,
      createdAt: adjustDate(createdAt),
    }))

    const ePrescriptions: OrderDraftEPrescriptionModelV4[] = cartStore.cartData.ePrescriptions.map(({ id, createdAt, numberOfMedications, note }) => ({
      id,
      note,
      numberOfMedications,
      createdAt: adjustDate(createdAt),
    }))

    return {
      // On checkout we only can send anonymousUserId or authorization header. Not both! Request with both will result with 403.
      anonymousUserId: anonymousUserId.value,
      pharmacyId: currentPharmacyStore.pharmacyId!,
      cart: {
        products,
        prescriptions,
        ePrescriptions,
        couponCode: couponStore.couponCode ?? null,
        myLife: cartStore.cartData.myLife,
      },
    }
  })

  const createDraft = async () => {
    const { token, refreshToken } = authStore
    const anonymousUserId = authStore.isAuthenticated ? undefined : anonymousUser.getToken()

    set(isLoading, true)
    const options = {
      token: token ?? '',
      refreshToken: refreshToken ?? '',
      language: locale,
    }

    const { success, data } = await createDraftAPI({ ...cartRequestData.value, anonymousUserId }, options)

    if (success && data) {
      setCheckoutStatus(data)
      draftId.value = data.draftId
      if (anonymousUserId)
        owner.value = anonymousUserId
    }

    set(isLoading, false)
    orderStore.reset()
  }

  const createDraftLegacy = async () => {
    const { cart, pharmacyId, anonymousUserId } = cartRequestData.value
    const { ePrescriptions, prescriptions, products, myLife } = cart
    const { token, refreshToken } = authStore

    const draftData = {
      // anonymousUserId is required for legacy API
      ePrescriptions: ePrescriptions.length
        ? [
            ...ePrescriptions.map(ep => ({ ...ep, anonymousUserId: anonymousUserId ?? '' })),
          ]
        : [],
      // anonymousUserId is required for legacy API
      prescriptions: prescriptions.length
        ? [
            ...prescriptions.map(p => ({ ...p, anonymousUserId: anonymousUserId ?? '' })),
          ]
        : [],
      products,
      myLife,
      anonymousUserId,
      pharmacyId,
    }

    const response = await createDraftLegacyAPI(draftData as OrderCreateDraftReqModel, {
      token: token ?? '',
      refreshToken: refreshToken ?? '',
      language: locale,
    })

    return response.success ? response.data?.draftId : undefined
  }

  const reset = () => {
    cartStore.reset()
    deliveryStore.reset()
    paymentStore.reset()
    couponStore.removeCoupon()
    draftId.value = undefined
    owner.value = undefined
    activeStep.value = undefined
    isPreviousStepCustomer.value = false
    setGuestData(undefined)
  }

  const updateDraft = async () => {
    // Only temporary, remove when all routes are migrated. Prevents updating draft (checkout v2 endpoint) on legacy routes.
    // That endpoint returns error 400 and all checkout stores are reset.

    if (cartStore.isCartEmpty || !currentPharmacyStore.pharmacyId || isLegacy.value)
      return

    set(isLoading, true)

    if (!draftId.value)
      await createDraft()

    if (orderStore.orderData.orderCode || !draftId.value)
      return

    const { token, refreshToken } = authStore
    const options = {
      token: token ?? '',
      refreshToken: refreshToken ?? '',
      language: locale,
    }

    const { cart } = cartRequestData.value
    const anonymousUserId = authStore.isAuthenticated ? undefined : anonymousUser.getToken()
    const { success, data } = await updateCartAPI({ anonymousUserId, cart }, draftId.value, options)

    set(isLoading, false)

    if (!success) {
      modalManager.show('CartEmptied')
      reset()
    }
    else if (data)
      setCheckoutStatus(data)

    set(isDataReady, true)
  }

  const storeGuestDetails = async (guestData: GuestData) => {
    if (!draftId.value || !anonymousUserId.value) {
      onShowLogs(() => console.log('Draft id or anonymousUserId is not defined. Cannot create guest details.'))
      return
    }

    const options = {
      apiKey: fecApiKey.value as string,
      language: locale,
    }

    const { salutation, email, firstName, lastName, phoneCountryCode, phoneNumberWithoutCountryCode } = guestData

    const payload: CustomerDetailsReqModelV4 = {
      anonymousUserId: anonymousUserId.value,
      customerDetails: {
        salutation,
        email,
        firstName,
        lastName,
        phone: {
          countryCode: phoneCountryCode,
          number: phoneNumberWithoutCountryCode,
        },
      },
    }

    set(isLoading, true)
    const { success, data } = await storeCustomerDetailsAPI(payload, draftId.value, options)
    set(isLoading, false)

    if (success && data)
      setCheckoutStatus(data)

    return success
  }

  if (import.meta.client) {
    http.events.on('checkout_forbidden', reset)
  }

  return {
    updateDraft,
    checkoutStatus,
    messages,
    setCheckoutStatus,
    productsPrice,
    nonPayableProductsPrice,
    couponPrice,
    onDutyPrice,
    deliveryPrice,
    totalCartPrice,
    totalPrice,
    draftId,
    owner,
    activeStep,
    guestData,
    setGuestData,
    isPreviousStepCustomer,
    isLoading,
    setIsLoading,
    isDataReady,
    storeGuestDetails,
    reset,
    createDraftLegacy,
  }
})
